import { LitElement, html, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js'
import { setRaiGlobalProperty, getStringOperatingSystem, isInIframe, utils, getRaiGlobalProperty } from './utilities'
import { getAdBlockDetector } from '../../../shared/utilities/utils';
import { raiPlayerEvents } from './player-events';

import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import './vendor/webtrekk_v4.min.js';


const unsubscribe$ = new Subject<void>();
let watcherPlayer = false;

const DEBUG_STR = 'WT';
const trackEnqueue = window.Rai!._trackEnqueue;

export class WebtrekkWC extends LitElement {

  @property({ type: Object }) declare pageInfo?: any;
  @property({ type: Object }) declare pageInfoBaseData?: any;
  @property({ type: Object }) declare mediaInfo?: any;
  @property() declare mode?: string;

  private wt: any;
  private _q: ITrackEnqueue;
  private mediaLibraryLoaded = false;
  private adBlock = false;
  private mediaParams: any;
  private handledEvents: string[] = [];
  private subscription: Subscription | undefined;

  constructor() {
    super();
    this.pageInfo = undefined;
    this.mediaInfo = undefined;
    this.mode = undefined;
    this.pageInfoBaseData = undefined;
    this.wt = new window.webtrekkV3(window.webtrekkConfig);
    setRaiGlobalProperty('WtInstance', this.wt);
    this._q = trackEnqueue;
  }

  override updated(_changedProperties: PropertyValues<this>) {
    super.updated(_changedProperties)
    if (this.mediaInfo && !this.mediaLibraryLoaded) {
      this.initMediaTrack();
    }
  }

  override render() {
    return html``;
  }

  override async firstUpdated(changedProperties: PropertyValues<this>) {
    super.firstUpdated(changedProperties);

    // Annulla eventuali sottoscrizioni precedenti
    if (this.subscription) {
        this.subscription.unsubscribe();
    }

    const adBlocker = getAdBlockDetector();
    const detected = adBlocker.detect()
    this.adBlock = detected;

    const page$ = this._q.pageQueue.pipe(
      filter(Boolean)
    );

    const fid19 = (await window.Rai?.FingerPrintManager?.getFid()).anonymized19;

    this.wt.setEverId(fid19);

    this.subscription = page$.pipe(
      takeUntil(unsubscribe$)
    ).subscribe((pageInfo: PageInfo | ActionData) => {
      if (pageInfo.mode === 'action' && 'linkId' in pageInfo && 'customClickParameter' in pageInfo) {
        const { linkId, customClickParameter } = pageInfo
        if (!linkId?.includes('BDO-landing:')) {
          this.trackAction({
            linkId, /* ct */
            customClickParameter, /* ck[1,2,3] */
          });
        }
      } else {
        if (this.mode === 'extendInfoByComponent') {
          pageInfo = { ...this.pageInfoBaseData, ...pageInfo };
        }
        this.pageInfo = pageInfo;
        this.trackPage();
      }
    })
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.subscription) {
        this.subscription.unsubscribe();
    }
    unsubscribe$.next();
    unsubscribe$.complete();
  }

  getMediaInfo(liveOverride: any) {
    const mediaInfo = liveOverride || this.mediaInfo; /* se presenti utilizzo info provenienti da onAir, esposte dal player */
    const track_info = mediaInfo.track_info;

    function get_mg10(_info: typeof track_info) {
      if (_info.genres?.length) {
        return _info.genres[0]
      } else if (_info.program_genres?.length) {
        return _info.program_genres[0]
      } else {
        return null;
      }
    }

    function get_mg11(_info: typeof track_info) {
      if (_info.sub_genres?.length) {
        return _info.sub_genres[0]
      } else if (_info.program_sub_genres?.length) {
        return _info.program_sub_genres[0]
      } else {
        return null;
      }
    }

    const mg = [
      !!document.referrer && document.referrer.indexOf('msn.com') > -1
        ? 'msn'
        : 'web' /*--mg1--*/,
      track_info.channel /*--mg2--*/,
      track_info.editor /*--mg3--*/,
      track_info.program_title /*--mg4--*/,
      track_info.media_type /*--mg5--*/,
      track_info.form /*--mg6--*/,
      track_info.episode_title || track_info.title /*--mg7--*/,
      track_info.season || track_info.edit_year || track_info.year /*--mg8--*/,
      track_info.episode_number /*--mg9--*/,
      get_mg10(track_info),
      get_mg11(track_info),
      track_info.typology /*--mg12--*/,
      track_info.domain /*--mg13--*/,
    ].reduce((acc, currVal, index, mg) => {
      if (currVal) {
        return (
          acc + 'mg' + (index + 1) + '=' + currVal + (index + 1 == mg.length ? '' : ';')
        );
      } else return acc;
    }, '');

    const mi = [
      !!document.referrer && document.referrer.indexOf('msn') > -1
        ? 'msn'
        : 'web',
      track_info.domain,
      track_info.media_type,
      track_info.program_title,
      track_info.episode_title || track_info.title,
      track_info.id,
    ].filter((val) => {
      return !!val;
    }).join(' - ');

    this.mediaParams = [
      mi /* mi */,
      'playerEvent' /* mk*/,
      'currentEventTime' /* mt1 */,
      'mediaDuration' /* mt2 */,
      mg /* mg[1/13] */,
      null /* bw = bandwith */,
      'event.volume' /* vol */,
      'event.muted' /* mut */,
    ];
    return mediaInfo;
  }

  initMediaTrack() {
    import(/* @vite-ignore */__public_path__ + '/common/assets/js/webcomponents/webtrekk-media-library.js').then(() => {
      this.mediaLibraryLoaded = true;
      this.wt.sendinfo_media = window.wt_sendinfo_media;

      const events = raiPlayerEvents.media;
      this.handledEvents = [
        events.READY,
        events.FIRSTPLAY,
        events.PLAY,
        events.ADS.STARTED,
        events.PAUSE,
        events.SEEK_END,
        events.ADS.SKIPPED,
        events.HEARTBEAT,
        events.ENDED,
        events.DISPOSE,
        events.PROGRAM_INFO_UPDATED,

        events.AUDIO_CHANGED,
        events.SUBTITLE_CHANGED,
        events.PIP_START,
        events.PIP_STOP,
        events.FULLSCREEN_EVENT,

        events.DRM_LICENSE_STATUS_INTERNAL_ERROR,
        events.ERROR,
        events.MEDIAPOLIS_PARSE_ERROR,
        events.NOT_SUPPORTED_MEDIA_TYPE,
        events.ADS.ADSERROR,
      ];

      window.wt_init_media(this.wt.trackDomain, this.wt.trackId);

      trackEnqueue.mediaQueue /* media track */
        .pipe(
          filter((plEvent: {type: string}) => this.handledEvents.includes(plEvent.type)),
          takeUntil(unsubscribe$)
        )
        .subscribe((plEvent: {type: string; detail: any}) => {
          try {
            this.trackMedia(plEvent.detail);
          } catch (e) {
            console.error(e);
          }
        });
    });
  }

  trackPage() {
    if (
      this.pageInfo.domain === 'rainews' &&
      this.pageInfo.content === 'homepage' &&
      this.pageInfo.traffic_origin !== 'app'
    ) {
      if (!document.hasFocus()) this.pageInfo.channel = 'nofocus';
    }
    const pageInfo = this.pageInfo;
    const wt = this.wt;
    const getCustomParam = () => {
      let customParam = getStringOperatingSystem() + ';';
      customParam +=
        'adBlock:' +
        this.adBlock +
        (this.pageInfo.customParam ? ';' + this.pageInfo.customParam : '');
      if (isInIframe()) {
        const iframeUrl = typeof document.referrer == 'string' ? document.referrer : 'unknown';
        customParam += ';iframe=' + iframeUrl;
      }
      if (typeof window.predictHpType === 'string') {
        customParam += ';predictHpType=' + window.predictHpType;
      } else {
        try {
          const predictHpRef = utils.cookie.get('predictPageRef');
          if (predictHpRef) {
            customParam += ';predictHpType=' + predictHpRef;
          }
          utils.cookie.remove('predictPageRef');
        } catch (e) {
          // pass
        }
      }
      return customParam;
    };
    const cleanGroup = function (arr: string[] | Record<number, string>) {
      let array: string[] = [];
      if (Array.isArray(arr)) {
        array = arr
      } else if (typeof arr === 'object' && !Array.isArray(arr)) {
        array = Array.from({ ...arr, length: Object.keys(arr).length })
      }
      const result = array.reduce((res: string[], it) => {
        if (it != null) {
          if (typeof it !== 'string') {
            it = `${it}`
          }
          it = it.toLowerCase().replace(/['`’]/g, '')
        }
        res.push(it);
        return res;
      }, []);
      return result;
    };

    let contentGroup = [
      null,
      'aggregato' /* cg1 */,
      pageInfo.domain /* cg2 */,
      pageInfo.typology || pageInfo.program_typology /* cg3 */,
      pageInfo.program_title /* cg4 */,
      pageInfo.content /* cg5 */,
      null /* cg6 */,
      null /* cg7 */,
      pageInfo.program_title /* cg8 */,
      pageInfo.section /* cg9 */,
      pageInfo.sub_section /* cg10 */,
      !!pageInfo.genres && pageInfo.genres.length
        ? pageInfo.genres[0]
        : pageInfo.program_genres
        ? pageInfo.program_genres[0]
        : null /* cg11 */,
      !!pageInfo.sub_genres && pageInfo.sub_genres.length
        ? pageInfo.sub_genres[0]
        : pageInfo.program_sub_genres
        ? pageInfo.program_sub_genres[0]
        : null /* cg12 */,
      pageInfo.year /* cg13*/,
      pageInfo.edition /* cg14 */,
    ];
    let customParameter = [
      null,
      /[\\?&]refresh_ce/.test(window.location.search)
        ? 'refresh_ce'
        : 'no-refresh' /* cp1 */,
      pageInfo.channel /* cp2 */,
      pageInfo.date /* cp3 */,
      pageInfo.page_type /* cp4 */,
      !!pageInfo.genres && pageInfo.genres.length /* cp5 */
        ? pageInfo.genres.toString().replace(/,/g, ';') +
          (!!pageInfo.sub_genres && pageInfo.sub_genres.length
            ? ';' + pageInfo.sub_genres.toString().replace(/,/g, ';')
            : '') +
          ';' +
          getCustomParam()
        : !!pageInfo.program_genres && pageInfo.program_genres.length
        ? pageInfo.program_genres.toString().replace(/,/g, ';') +
          (!!pageInfo.program_sub_genres && pageInfo.program_sub_genres.length
            ? ';' + pageInfo.program_sub_genres.toString().replace(/,/g, ';')
            : '') +
          ';' +
          getCustomParam()
        : getCustomParam(),
      pageInfo.season /* cp6 */,
      pageInfo.episode_title /* cp7 */,
      pageInfo.episode_number /* cp8 */,
      (!!window.startLoadDate && Date.now() - window.startLoadDate) ||
        null /* cp9 */,
      pageInfo.traffic_origin || 'browsing' /* cp10 */,
      pageInfo.device || 'browser' /* cp11 */,
      pageInfo.platform != '[platform]' ? pageInfo.platform : 'web' /* cp12 */,
      pageInfo.editor /* cp13 */,
      pageInfo.typology /* cp14 */,
    ];

    contentGroup = cleanGroup(contentGroup);
    customParameter = cleanGroup(customParameter);

    wt.contentId =
      document.location.protocol +
      '//' +
      document.location.host +
      document.location.pathname;
    wt.contentGroup = contentGroup; /*dati editoriali*/
    wt.customParameter = customParameter;
    wt.internalSearch = pageInfo.cerca_per;
    const Raisso = getRaiGlobalProperty<Raisso>('Raisso');
    const ProfileManager = getRaiGlobalProperty<ProfileManager>('ProfileManager');
    const raiSsoUserObject = !!ProfileManager && ProfileManager.getUserProfile();

    if (!!Raisso && Raisso.checkLogin() && !!raiSsoUserObject) {
      /*dati utente*/
      if (
        !!raiSsoUserObject['birthYear'] &&
        !!raiSsoUserObject['age'] &&
        raiSsoUserObject['age'] >= 18
      ) {
        wt.birthdayJ = raiSsoUserObject['birthYear'];
        wt.birthdayM = '01'; /* uc707*/
        wt.birthdayD = '01';
      }
      wt.customerId = raiSsoUserObject['ua'] || null; /* cd */
      /* uc706 */
      if (raiSsoUserObject['gender']) {
        wt.gender = raiSsoUserObject['gender'].toLowerCase() === 'm' ? '1' : '2'
      } else {
        wt.gender = '3'
      }
      wt.urmCategory = {
        1: raiSsoUserObject['personalizzazione'] ? 'true' : 'false' /* uc1 */,
      };
    }

    try {
      wt.sendinfo();
    } catch (err) {
      // pass
    }
  }

  trackAction(actionInfo: any) {
    const wt = this.wt;
    wt.contentId =
      document.location.protocol +
      '//' +
      document.location.host +
      document.location.pathname;
    wt.sendinfo(actionInfo);
  }

  trackMedia(event: any) {
    if (!event.eventData) throw 'Missing player event data';
    const eventInfo = event.eventData;
    const events = raiPlayerEvents.media;
    const sendinfo_media = this.wt.sendinfo_media;

    const paramsPrev = this.mediaParams ? [...this.mediaParams] : []; //dal secondo evento immagazzinerà le mediaInfo dell'evento precedente.

    if (
      eventInfo.eventName == events.PROGRAM_INFO_UPDATED ||
      eventInfo.eventName == events.READY
    ) {
      this.getMediaInfo(event.mediaInfo.current_program_info); /*aggiorno mediaInfo solo su cambio di media*/
    }

    const track_info = this.mediaInfo.track_info;
    const params = [...this.mediaParams];

    paramsPrev[2] = Math.round(eventInfo.currentTime);
    paramsPrev[3] = Math.round(eventInfo.mediaDuration);
    paramsPrev[6] = eventInfo.volume;
    paramsPrev[7] = eventInfo.muted;

    params[2] = Math.round(eventInfo.currentTime);
    params[3] = Math.round(eventInfo.mediaDuration);
    params[6] = eventInfo.volume;
    params[7] = eventInfo.muted;

    switch (eventInfo.eventName) {
      case events.READY:
        params[1] = 'init';
        sendinfo_media(...params);

        params[1] = track_info.media_type;
        sendinfo_media(...params);

        params[1] = 'web';
        break;
      case events.FIRSTPLAY:
        params[1] = 'start content';
        sendinfo_media(...params);

        params[1] = 'play';

        if (!watcherPlayer) {
          sendinfo_media(
            ...params
          ); /**invio "play". altrimenti sovrascritto da play300 */
          watcherPlayer = true;
          const interv = setInterval(function () {
            params[1] = 'play300';
            sendinfo_media(...params);
            clearInterval(interv);
          }, 300);
        }

        break;
      case events.PLAY:
        params[1] = 'play';
        break;
      case events.ADS.STARTED:
        params[1] =
          'spot ' + eventInfo.mediaType + ' ' + eventInfo.sequenceIndex;
        break;
      case events.PAUSE:
        params[1] = 'pause';
        break;
      case events.SEEK_END:
        params[1] = 'seek';
        break;
      case events.ADS.SKIPPED:
        params[1] = 'vastSkipped';
        break;
      case events.HEARTBEAT:
        if (track_info.media_type == 'live') {
          params[1] = 'uptime';
          sendinfo_media(...params);
        }
        params[1] = 'pos';
        break;
      case events.ENDED:
        params[1] = 'eof';
        break;
      case events.DISPOSE:
        params[1] = 'stop';
        break;
      case events.PROGRAM_INFO_UPDATED:
        paramsPrev[1] = 'eof';
        sendinfo_media(...paramsPrev);

        params[1] = 'init';
        sendinfo_media(...params);

        params[1] = track_info.media_type;
        sendinfo_media(...params);

        params[1] = 'web';
        sendinfo_media(...params);

        params[1] = 'start content';
        sendinfo_media(...params);

        params[1] = 'play';
        break;
      case events.AUDIO_CHANGED:
        params[1] =
          !!eventInfo.additionalData && !!eventInfo.additionalData.audioTrack
            ? eventInfo.additionalData.audioTrack
            : 'audio changed';
        break;
      case events.SUBTITLE_CHANGED:
        params[1] =
          !!eventInfo.additionalData && !!eventInfo.additionalData.subtitleTrack
            ? eventInfo.additionalData.subtitleTrack
            : 'subtitle changed';
        break;
      case events.PIP_START:
      case events.PIP_STOP:
        params[1] =
          !!eventInfo.additionalData && !!eventInfo.additionalData.pipMode
            ? eventInfo.additionalData.pipMode
            : 'PIP changed';
        break;
      case events.FULLSCREEN_EVENT:
        params[1] = eventInfo.fullscreen ? 'fullscreen' : 'windowed';
        break;

      case events.DRM_LICENSE_STATUS_INTERNAL_ERROR:
      case events.ERROR:
      case events.MEDIAPOLIS_PARSE_ERROR:
      case events.NOT_SUPPORTED_MEDIA_TYPE:
      case events.ADS.ADSERROR:
        params[1] = 'ERROR: ' + eventInfo.eventName;
        watcherPlayer = false;
        break;
    }
    if (watcherPlayer) {
      watcherPlayer = false;
    } else if (eventInfo.mediaType) {
      sendinfo_media(...params);
    }
  }

  private traceDebug(...params: unknown[]) {
    console.log(DEBUG_STR, params);
  }
}
